import { PhoneNumberWithPrefixSchema } from "@/modules/box/boxTypes"
import { AddressDtoSchema } from "@modules/address/addressDto"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { PublicProfileDtoSchema } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { z } from "zod"

export enum MemberFilter {
  ALL = "all",
  PROSPECT = "prospect",
  ACTIVE = "active",
  TERMINATED = "terminated",
  STAFF = "staff",
  DROP_IN = "drop_in",
}

export enum MemberColumnToSort {
  NAME = "NAME",
  ACTIVE_OFFER = "ACTIVE_OFFER",
  LAST_VISIT = "LAST_VISIT",
  NO_SHOW_COUNT = "NO_SHOW_COUNT",
  LAST_MEMBERSHIP = "LAST_MEMBERSHIP",
  INSCRIPTION_DATE = "INSCRIPTION_DATE",
  ARCHIVED_DATE = "ARCHIVED_DATE",
}

export enum MemberStatus {
  ACTIVE = "ACTIVE",
  PROSPECT = "PROSPECT",
  TERMINATED = "TERMINATED",
  DROP_IN = "DROP_IN",
}

export const MemberActiveOfferDtoSchema = z
  .object({
    offerName: z.string(),
    endValidity: z.string().nullish(),
    endSubscription: z.string().nullish(),
    subscriptionStatus: z.enum(BoxSubscriptionStatus).nullish(),
  })
  .meta({ id: "MemberActiveOfferDtoSchema" })

export const MemberItemDtoSchema = z
  .object({
    id: z.string(),
    avatar: HUImageDtoSchema.nullish(),
    firstname: z.string(),
    lastname: z.string(),
    username: z.string(),
    status: z.enum(MemberStatus),
    note: z.string().nullish(),
    lastVisited: z.iso.datetime().nullish(),
    noShowPastMonth: z.number().nullish(),
    activeOffers: z.array(MemberActiveOfferDtoSchema),
    archivedDate: z.string().nullish(),
    inscriptionDate: z.iso.datetime().nullish(),
    archived: z.boolean().nullish(),
    lastMembership: z.string().nullish(),
  })
  .meta({ id: "MemberItemDtoSchema" })

export const BoxMemberNextBookingDtoSchema = z
  .object({
    slotId: z.string(),
    name: z.string(),
    date: z.iso.datetime(),
    color: z.string(),
  })
  .meta({ id: "BoxMemberNextBookingDtoSchema" })

export const MemberDetailedDraftDtoSchema = z
  .object({
    firstname: z.string(),
    lastname: z.string(),
    username: z.string().nullish(),
    birthDate: z.string().nullish(),
    phone: z.string(),
    email: z.string(),
    address: AddressDtoSchema.nullable(),
    emergencyInformation: z.string(),
    note: z.string(),
    activeOffersTitles: z.array(z.string()).nullish(),
  })
  .meta({ id: "MemberDetailedDraftDtoSchema" })

export const MemberDetailedUpadteInputSchema = z
  .object({
    firstname: z.string().nullish(),
    lastname: z.string().nullish(),
    birthDate: z.string().nullish(),
    phoneWithPrefix: PhoneNumberWithPrefixSchema.nullable().nullish(),
    address: AddressDtoSchema.nullable().nullish(),
    emergencyInformation: z.string(),
    note: z.string(),
  })
  .meta({ id: "MemberDetailedUpadteInputSchema" })

export const MemberDetailedDtoSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    firstname: z.string().nullable(),
    lastname: z.string().nullable(),
    username: z.string(),
    inscriptionDate: z.string(),
    lastVisited: z.string().nullable(),
    birthDate: z.string().nullable(),
    phoneWithPrefix: PhoneNumberWithPrefixSchema.nullable(),
    address: AddressDtoSchema.nullable(),
    email: z.string(),
    emergencyInformation: z.string().nullable(),
    status: z.enum(MemberStatus),
    note: z.string().nullable(),
    noShowPastMonth: z.number().nullable(),
    nextBookings: z.array(BoxMemberNextBookingDtoSchema).nullable(),
    publicProfile: PublicProfileDtoSchema.nullable(),
    activeOffersTitles: z.array(z.string()).nullable(), // deprecated
  })
  .meta({ id: "MemberDetailedDtoSchema" })

export type MemberDetailedUpadteInput = z.infer<typeof MemberDetailedUpadteInputSchema>
export type MemberDetailedDraftDto = z.infer<typeof MemberDetailedDraftDtoSchema>
export type MemberItemDto = z.infer<typeof MemberItemDtoSchema>
export type BoxMemberNextBookingDto = z.infer<typeof BoxMemberNextBookingDtoSchema>
export type MemberActiveOfferDto = z.infer<typeof MemberActiveOfferDtoSchema>
export type MemberDetailedDto = z.infer<typeof MemberDetailedDtoSchema>

export enum SESSION_HISTORY_STATUS {
  PRESENT = "PRESENT",
  NO_SHOW = "NO_SHOW",
}

export enum DAY_OF_WEEK {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

// Member Session History
export const MemberSessionHistoryItemDtoSchema = z
  .object({
    name: z.string(),
    color: z.string(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    status: z.enum([SESSION_HISTORY_STATUS.PRESENT, SESSION_HISTORY_STATUS.NO_SHOW]),
    archived: z.boolean(),
    offerName: z.string(),
    offerType: z.string(), // BoxOfferType usually maps to string enum like "SUBSCRIPTION"|"PACK" internally, but we'll accept string
    coachName: z.string().nullable(),
    bookedAt: z.iso.datetime(),
    location: z.string().nullable(),
  })
  .meta({ id: "MemberSessionHistoryItemDtoSchema" })

export type MemberSessionHistoryItemDto = z.infer<typeof MemberSessionHistoryItemDtoSchema>

export const MemberSessionHistoryListDtoSchema = z
  .array(MemberSessionHistoryItemDtoSchema)
  .meta({ id: "MemberSessionHistoryListDtoSchema" })

// Member Session History Summary
export const SessionHistoryProgramDtoSchema = z
  .object({
    name: z.string(),
    color: z.string(),
    count: z.number(),
    archived: z.boolean().optional(),
  })
  .meta({ id: "SessionHistoryProgramDtoSchema" })

export const SessionHistoryFavoriteSlotDtoSchema = z
  .object({
    name: z.string(),
    color: z.string(),
    dayOfWeek: z.enum(DAY_OF_WEEK),
    startTime: z.iso.time(),
    endTime: z.iso.time(),
    count: z.number(),
    archived: z.boolean().optional(),
  })
  .meta({ id: "SessionHistoryFavoriteSlotDtoSchema" })

export const MemberSessionHistorySummaryDtoSchema = z
  .object({
    pageCounter: z.object({
      all: z.number(),
      present: z.number(),
      noShow: z.number(),
    }),
    resume: z.array(SessionHistoryProgramDtoSchema),
    favorites: z.array(SessionHistoryFavoriteSlotDtoSchema),
  })
  .meta({ id: "MemberSessionHistorySummaryDtoSchema" })

export type SessionHistoryProgramDto = z.infer<typeof SessionHistoryProgramDtoSchema>
export type SessionHistoryFavoriteSlotDto = z.infer<typeof SessionHistoryFavoriteSlotDtoSchema>
export type MemberSessionHistorySummaryDto = z.infer<typeof MemberSessionHistorySummaryDtoSchema>
